// src/components/FormSections/SectionCollapsibleCard.tsx

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  title: string;
  message?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  /**
   * Incrementar este valor desde el formulario padre fuerza la expansión de
   * la tarjeta (por ejemplo, cuando el envío falló por un error de
   * validación y el campo afectado está dentro de esta sección colapsada).
   * El usuario puede volver a colapsarla normalmente después.
   */
  forceOpenToken?: number;
}

const SectionCollapsibleCard: React.FC<Props> = ({
  title,
  message,
  children,
  defaultOpen = false,
  forceOpenToken,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (forceOpenToken !== undefined) {
      setOpen(true);
    }
  }, [forceOpenToken]);

  return (
    <div
      className="
        rounded-md

        border
        border-stroke

        bg-white

        shadow-default

        transition-all
        duration-300

        dark:border-strokedark
        dark:bg-boxdark
      "
    >
      {/* HEADER */}

      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="
          flex

          w-full
          cursor-pointer
          select-none
          items-center

          justify-between
          gap-2

          bg-gray-2
          px-3

          py-3

          text-left

          transition

          hover:bg-gray

          dark:bg-boxdark-2
          dark:hover:bg-meta-4

          sm:px-4
          sm:py-4
        "
      >
        <div className="flex flex-col items-start">
          <h2
            className="
              break-words

              text-sm
              font-medium

              leading-tight
              text-black

              dark:text-white
              sm:text-base
            "
          >
            {title}
          </h2>

          {message && (
            <p
              className="
                mt-0.5
                max-w-full

                break-words

                text-xs
                leading-tight

                text-body
                dark:text-bodydark1

                sm:text-xs
              "
            >
              {message}
            </p>
          )}
        </div>

        {/* Icon responsive */}

        <div className="flex-shrink-0">
          {open ? (
            <ChevronUp
              className="
                text-primary
              "
              size={18}
            />
          ) : (
            <ChevronDown
              className="
                text-body
                dark:text-bodydark1
              "
              size={18}
            />
          )}
        </div>
      </button>

      {/* CONTENT */}

      {open && (
        <div
          className="
            border-t
            border-stroke

            px-3

            py-3
            text-sm

            dark:border-strokedark
            sm:px-4

            sm:py-4
            sm:text-base
          "
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default SectionCollapsibleCard;
